import { createInitialConsentState, reduceConsent } from "@/core/consent/domain/consent.reducer";
import type { ConsentSignal } from "@/core/consent/domain/consent.signals";
import type { ConsentStatus } from "@/core/consent/domain/consent.types";
import type { ConsentSource } from "@/core/consent/sources/consent.source";
import { persistResolvedConsent } from "@/core/consent/sources/storage.source";

/**
 * The read side of the engine — the one interface everything downstream
 * depends on. The React provider projects it, the instrumentation bus is
 * wired to it by the composition root, and the decision recorder observes
 * it. None of them know how a status came to be.
 */
export type ConsentStatusStore = {
  readonly getStatus: () => ConsentStatus;
  /**
   * Notified AFTER the engine's state has moved, with the new status, so a
   * listener reading `getStatus` in response always sees it. Not retroactive:
   * a listener subscribed after a resolution learns of it only through
   * `getStatus`, never through a replayed notification.
   */
  readonly subscribe: (listener: (status: ConsentStatus) => void) => () => void;
};

export type ConsentEngine = ConsentStatusStore & {
  /**
   * Start the sources. Deliberately separate from construction so every
   * observer can subscribe first: the storage source emits synchronously on
   * start, and the recorder must see that very first resolution.
   */
  readonly start: () => void;
  readonly stop: () => void;
};

export type CreateConsentEngineOptions = {
  readonly sources: readonly ConsentSource[];
  readonly noticeVersion: string;
};

/**
 * The headless consent engine: sources → reducer → observers, with
 * persistence of each newly resolved state as the one side effect the
 * reducer itself never performs. Nothing here depends on React, on a vendor,
 * or on the instrumentation bus — everything observable is reachable through
 * `getStatus` and `subscribe`, which is what keeps the whole stack testable
 * without a DOM tree and lets the bus be one observer among several.
 */
export const createConsentEngine = ({
  sources,
  noticeVersion,
}: CreateConsentEngineOptions): ConsentEngine => {
  let state: ConsentStatus = createInitialConsentState(noticeVersion);
  let started = false;
  let stopped = false;
  let sourceStops: readonly (() => void)[] = [];
  const listeners = new Set<(status: ConsentStatus) => void>();

  const notify = (status: ConsentStatus): void => {
    for (const listener of listeners) {
      listener(status);
    }
  };

  const commit = (next: ConsentStatus): void => {
    if (next.status === "resolved") {
      // Persisted BEFORE observers see it so an observer crashing the page
      // cannot lose the decision. Re-persisting a restored decision rewrites
      // an identical blob — idempotent by construction.
      persistResolvedConsent(next);
    }
    notify(next);
  };

  // While the sources are starting, signals fold into state WITHOUT reaching
  // observers. The reducer is order-independent, but activation downstream is
  // not: a stored grant arriving before the GPC source's assertion would
  // activate analytics vendors — Datadog cannot un-initialize — that the
  // settled state then denies. One consolidated notification after every
  // source has started makes startup depend only on the settled state, never
  // on the order sources appear in the composition array.
  let settling = false;

  const emit = (signal: ConsentSignal): void => {
    if (stopped) {
      return;
    }

    const next = reduceConsent(state, signal);
    // Same reference means the signal changed nothing — no persistence, no
    // notification, no re-render downstream.
    if (next === state) {
      return;
    }

    state = next;
    if (!settling) {
      commit(next);
    }
  };

  return {
    getStatus: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    start: () => {
      if (started || stopped) {
        return;
      }
      started = true;

      const initialState = state;
      settling = true;
      sourceStops = sources.map((source) => source.start(emit));
      settling = false;
      // Widened explicitly: control-flow narrowing cannot see that `emit`
      // reassigns `state` inside the map above.
      const settled = state as ConsentStatus;
      if (settled !== initialState) {
        commit(settled);
      }
    },
    stop: () => {
      if (stopped) {
        return;
      }
      stopped = true;
      for (const stopSource of sourceStops) {
        stopSource();
      }
      listeners.clear();
    },
  };
};
